import { BtnPrimary } from '@components/buttons';
import { BaseDialog } from '@components/dialog';
import { showModalForm } from '@components/modal-form';
import { categoryLabel } from './file-thumbnail';
import { formatBytes } from 'shared/formatting';
import { PreviewContent } from './preview-modal';
import { DuplicateMatch } from './state';
import { UsagePanel } from './usage-panel';

/**
 * Outcome of the "you've uploaded this before" prompt. `reuse` carries the
 * existing file to pick instead of uploading; `upload` proceeds with a fresh
 * upload; `cancel` aborts the whole pick (modal dismissed).
 */
export type ReuseChoice =
  | { type: 'reuse'; file: DuplicateMatch }
  | { type: 'upload' }
  | { type: 'cancel' };

function ReusePromptModal({
  match,
  onChoose,
}: {
  match: DuplicateMatch;
  onChoose(choice: ReuseChoice): void;
}) {
  const sizeLabel = formatBytes(match.size);
  const usage = match.usageCourseCount;

  return (
    <BaseDialog
      onClose={() => onChoose({ type: 'cancel' })}
      contentWidth
      blurBg={false}
      compact
      zIndex="z-[70]"
    >
      <section class="w-2xl max-w-full max-h-[90vh] bg-white rounded-lg overflow-hidden flex flex-col">
        <header class="px-6 pt-5 pb-4 shrink-0">
          <h2 class="text-base font-semibold text-gray-900">
            {match.isExactMatch
              ? "You've uploaded this file before"
              : 'You may already have this video'}
          </h2>
          <p class="mt-1 text-sm text-gray-600">
            {match.isExactMatch
              ? "Reuse it and it's ready to use instantly — no waiting for the upload or video processing to finish. You'll also keep your library tidy and skip storing the same file twice."
              : "This matches a video already in your library. If it's the same one, reuse it and it's ready instantly — no waiting for the upload or video processing to finish — and you won't store the same video twice."}
          </p>
        </header>
        <div class="flex-1 min-h-0 overflow-auto bg-gray-50 border-y border-gray-200">
          <div class="p-4 flex items-center justify-center">
            <PreviewContent file={match} />
          </div>
          <UsagePanel fileId={match.id} />
        </div>
        <div class="px-6 pt-4 flex flex-col gap-0.5 shrink-0">
          <span class="text-sm font-medium text-gray-900 truncate">{match.name}</span>
          <span class="text-xs text-gray-500">
            {categoryLabel(match.type)}
            {sizeLabel ? ` · ${sizeLabel}` : ''}
            {usage > 0 ? ` · used in ${usage} course${usage === 1 ? '' : 's'}` : ''}
          </span>
        </div>
        <footer class="px-6 pb-5 pt-4 flex flex-col items-center gap-3 shrink-0">
          <BtnPrimary
            class="rounded-full py-2.5 px-10"
            onClick={() => onChoose({ type: 'reuse', file: match })}
          >
            Use this file
          </BtnPrimary>
          <button
            type="button"
            class="text-xs text-gray-500 hover:text-gray-800 hover:underline"
            onClick={() => onChoose({ type: 'upload' })}
          >
            No thanks, upload a new copy
          </button>
        </footer>
      </section>
    </BaseDialog>
  );
}

/**
 * Surfaces the matching file (the most recently used one) so the user can reuse
 * it. Resolves with the user's choice; a dismissed modal counts as `cancel`.
 */
export async function showReusePrompt(match: DuplicateMatch): Promise<ReuseChoice> {
  const choice = await showModalForm<ReuseChoice>(({ resolve }) => (
    <ReusePromptModal match={match} onChoose={resolve} />
  ));
  return choice ?? { type: 'cancel' };
}
